import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PAYOUT_RAILS, formatINR } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import CustomerWalletPanel from "@/components/CustomerWalletPanel";
import { trackEvent } from "@/lib/analytics";
import {
  Smartphone, Send, ChevronRight, CheckCircle2, Plus, Copy, ExternalLink, AlertCircle, Loader2, ShieldCheck, ArrowLeft, MessageCircle, Trash2, Download, GraduationCap, UserCircle2, Link2,
} from "lucide-react";

const loadedScripts = new Set();
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (loadedScripts.has(src)) return resolve();
    const s = document.createElement("script");
    s.src = src; s.async = true;
    s.onload = () => { loadedScripts.add(src); resolve(); };
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(s);
  });
}

export default function Payouts() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isEducation = (searchParams.get("mode") || "").toLowerCase() === "education"
    || (location.pathname || "").startsWith("/education-fees");
  const [step, setStep] = useState(1);
  const [isSelf, setIsSelf] = useState(true); // Phase 7.37h — default to self-payout
  const [amountLocked, setAmountLocked] = useState(false); // Phase 7.37h — reveals pay-mode inline

  // Education-fee state (Phase 7.37g) — only used when isEducation = true
  const [eduCategories, setEduCategories] = useState([]);
  const [eduCategory, setEduCategory] = useState("");
  const [studentName, setStudentName] = useState("");
  const [studentClass, setStudentClass] = useState("");
  const [studentRoll, setStudentRoll] = useState("");
  // Tutor mobile + hitch.zone KYC check (Phase 7.37g)
  const [tutorMobile, setTutorMobile] = useState("");
  const [tutorKyc, setTutorKyc] = useState(null); // { verification_percentage, threshold, is_verified, full_name, pan_number }
  const [tutorKycBusy, setTutorKycBusy] = useState(false);
  // "Import verified account" picker + "Send KYC link" share modal
  const [tutorAccounts, setTutorAccounts] = useState(null); // null = not loaded, [] = loaded empty
  const [tutorAccountsBusy, setTutorAccountsBusy] = useState(false);
  const [kycLinkData, setKycLinkData] = useState(null); // { url, whatsapp_url, sms_text }
  const [kycLinkBusy, setKycLinkBusy] = useState(false);

  // Step state
  const [customerMobile, setCustomerMobile] = useState("");
  const [otpChannel, setOtpChannel] = useState("sms"); // sms | whatsapp
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [devOtp, setDevOtp] = useState(null);
  const [whatsappUrl, setWhatsappUrl] = useState("");
  const [reusedSession, setReusedSession] = useState(false);

  const [amount, setAmount] = useState("");
  const [rail, setRail] = useState("upi");
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [beneId, setBeneId] = useState("");

  const [gateway, setGateway] = useState("razorpay");
  const [paymentMethod, setPaymentMethod] = useState("in_shop");
  const [pgFee, setPgFee] = useState(0);

  const [subscription, setSubscription] = useState(null);
  const [gwConfig, setGwConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  // Phase 7.20 — KYC banner (blocked at /customer/send-otp)
  const [kycBlocked, setKycBlocked] = useState(null);

  // bene-add inline modal
  const [addBeneOpen, setAddBeneOpen] = useState(false);
  const [bneNickname, setBneNickname] = useState("");
  const [bneType, setBneType] = useState("upi");
  const [bneUpi, setBneUpi] = useState("");
  const [bneAcc, setBneAcc] = useState("");
  const [bneIfsc, setBneIfsc] = useState("");
  const [bneHolder, setBneHolder] = useState("");
  // Listed (parent-system) accounts the partner can pick from
  const [hitchAccounts, setHitchAccounts] = useState([]);
  const [hitchAccountsLoading, setHitchAccountsLoading] = useState(false);
  // Hitch.zone "Import saved accounts" modal
  const [hitchImportOpen, setHitchImportOpen] = useState(false);
  const [hitchImportStep, setHitchImportStep] = useState("idle"); // idle | otp_sent | done
  const [hitchOtp, setHitchOtp] = useState("");
  const [hitchImportBusy, setHitchImportBusy] = useState(false);
  // Inline hitch.zone OTP for add-bene flow (when session expired)
  const [addBeneOtpStep, setAddBeneOtpStep] = useState("idle"); // idle | otp_required
  const [addBeneOtp, setAddBeneOtp] = useState("");

  // Link result
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, g] = await Promise.all([api.get("/me/subscription"), api.get("/wallet/gateways/config")]);
        setSubscription(s.data); setGwConfig(g.data);
        if (g.data?.razorpay?.enabled) setGateway("razorpay");
        else if (g.data?.cashfree?.enabled) setGateway("cashfree");
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
      }
    })();
  }, []);

  // Recompute PG fee whenever amount/rail/method/sub changes — must mirror backend `fee_for`
  // composite = slab_fee (in_shop|link) + rail_flat_fee + pg_fee_percent% + otp_fee
  useEffect(() => {
    if (!subscription || !amount) { setPgFee(0); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setPgFee(0); return; }
    const slabs = subscription.rail_slabs?.[rail] || [];
    const matched = slabs.find((s) => amt >= s.min && amt <= s.max) || slabs[slabs.length - 1];
    const slabFee = matched ? (paymentMethod === "link" ? matched.link_fee : matched.in_shop_fee) : 0;
    const railFeeSlabs = subscription.rail_fees?.[rail] || [];
    const railMatched = railFeeSlabs.find((s) => amt >= s.min && amt <= s.max);
    const railFlat = railMatched ? railMatched.fee : 0;
    const pct = (subscription.pg_fee_percent || 0) * amt / 100;
    const otpFee = subscription.otp_fee || 0;
    setPgFee(Math.round((slabFee + railFlat + pct + otpFee) * 100) / 100);
  }, [amount, rail, paymentMethod, subscription]);

  // Reset amount lock whenever the partner toggles the pay-for mode or steps
  // back to a previous wizard step. Avoids stale pay-mode block leaking through.
  useEffect(() => { setAmountLocked(false); }, [isSelf]);
  useEffect(() => { if (step < 3) setAmountLocked(false); }, [step]);

  // Phase 7.37l — When the partner toggles off self-mode, clear any
  // pre-populated mobile (we set it to the partner's own number for the
  // self-mode "Continue to amount" shortcut). Customer-mode should always
  // start with an empty field.
  useEffect(() => {
    if (!isSelf && step === 1) {
      setCustomerMobile("");
      setOtpToken("");
      setOtpSent(false);
    }
  }, [isSelf, step]);


  useEffect(() => {
    if (!isEducation) return;
    (async () => {
      try {
        const res = await api.get("/education/categories");
        setEduCategories(res.data?.items || []);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Failed to load education categories");
      }
    })();
  }, [isEducation]);

  // Phase 7.37g — Live hitch.zone KYC check for the tutor (= bene recipient).
  // Surfaced as a "Check KYC" action next to the tutor-mobile input inside the
  // add-bene modal. We deliberately don't auto-fire on every keystroke — each
  // call hits the parent system, so we let the partner press the button.
  const checkTutorKyc = async () => {
    const cleaned = (tutorMobile || "").replace(/\D/g, "");
    if (cleaned.length !== 10) { toast.error("Enter a 10-digit tutor mobile"); return; }
    setTutorKycBusy(true);
    setTutorAccounts(null);
    setKycLinkData(null);
    try {
      const { data } = await api.get(`/education/tutor-kyc/${cleaned}`);
      setTutorKyc(data);
      if (data.is_verified) {
        toast.success(`KYC verified · ${data.verification_percentage}% · ${data.full_name || "name on file"}`);
        // Pre-fill account holder name from snapshot so the form is one tap shorter
        if (data.full_name && data.full_name.toUpperCase() !== "N/A" && !bneHolder) {
          setBneHolder(data.full_name);
        }
      } else {
        toast.warning(`KYC only ${data.verification_percentage}% — needs ≥ ${data.threshold}%`);
      }
    } catch (err) {
      setTutorKyc(null);
      toast.error(formatApiError(err.response?.data?.detail) || "KYC lookup failed");
    } finally {
      setTutorKycBusy(false);
    }
  };

  // Phase 7.37g+m — "Import verified account" — hard refresh via tutor OTP →
  // hitch.zone login → list_accounts. Tutor must enter the OTP sent to their
  // own mobile (we don't have their session otherwise). After verify, the
  // backend returns accounts_preview directly so the picker fills instantly.
  const [tutorOtpStep, setTutorOtpStep] = useState("idle"); // idle | sent | verifying
  const [tutorOtp, setTutorOtp] = useState("");
  const [tutorOtpBusy, setTutorOtpBusy] = useState(false);

  const loadTutorAccounts = async () => {
    if (!tutorMobile || tutorMobile.length !== 10) return;
    setTutorAccountsBusy(true);
    try {
      // Step 1: trigger hitch.zone OTP for the tutor (server forwards to hitch /login)
      await api.post("/customer/hitch-otp/send", { customer_mobile: tutorMobile });
      setTutorOtpStep("sent");
      toast.success(`OTP sent to tutor +91 ${tutorMobile}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send tutor OTP");
    } finally {
      setTutorAccountsBusy(false);
    }
  };

  const verifyTutorOtpAndLoadAccounts = async () => {
    if (!tutorOtp || tutorOtp.length < 4) { toast.error("Enter the OTP"); return; }
    setTutorOtpBusy(true);
    try {
      // Phase 7.37n — auto_save_as_tutor=true → server bulk-creates a
      // beneficiary row for every hitch account, dedupes by acct+ifsc, and
      // stamps tutor_mobile so the wizard groups them under the right tutor.
      // link_customer_mobile stamps customer_mobile so the bene shows up
      // under THIS customer in step 2 (which scopes by customer_mobile).
      const { data } = await api.post("/customer/hitch-otp/verify", {
        customer_mobile: tutorMobile, otp: tutorOtp,
        auto_save_as_tutor: true,
        link_customer_mobile: customerMobile || null,
      });
      const preview = data?.accounts_preview || { items: [], count: 0, saved_count: 0 };
      // Refresh the bene list using the SAME customer-scoped endpoint that
      // step 2 normally reads from, so the freshly saved rows surface inline.
      try {
        if (customerMobile) {
          const benesRes = await api.get(`/customer/${customerMobile}/beneficiaries`);
          setBeneficiaries(benesRes.data?.items || []);
        } else {
          const benesRes = await api.get(`/beneficiaries`);
          setBeneficiaries(benesRes.data?.items || []);
        }
      } catch { /* non-fatal */ }
      setTutorAccounts(null);
      setTutorOtpStep("idle");
      setTutorOtp("");
      // Close the Add-Tutor dialog — the partner can pick the newly saved
      // bene directly from the grouped tutor list in step 2.
      setAddBeneOpen(false);
      if ((preview.saved_count || 0) > 0) {
        // Phase 7.37n+2 — hitch.zone accounts are always bank. If the user
        // is still on the UPI rail (the wizard's default) they'd be filtered
        // out of the eligibleBenes list. Flip to IMPS so the tutor card is
        // immediately visible.
        if (rail === "upi") setRail("imps");
        toast.success(`${preview.saved_count} tutor account${preview.saved_count === 1 ? "" : "s"} saved · pick one above`);
      } else if (preview.count > 0) {
        if (rail === "upi") setRail("imps");
        toast.info(`All ${preview.count} accounts already saved — pick one from the tutor list`);
      } else if (preview.error) {
        toast.warning(`hitch.zone error: ${preview.error}`);
      } else {
        toast.info("No saved accounts at hitch.zone for this tutor.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invalid OTP");
    } finally {
      setTutorOtpBusy(false);
    }
  };

  const applyTutorAccount = (acc) => {
    setBneType(acc.type);
    setBneNickname(acc.nickname || (tutorKyc?.full_name ? `${tutorKyc.full_name.split(" ")[0]}_tutor` : ""));
    if (acc.type === "bank") {
      setBneAcc(acc.account_number || "");
      setBneIfsc((acc.ifsc || "").toUpperCase());
      setBneHolder(acc.account_holder_name || tutorKyc?.full_name || "");
    } else {
      setBneUpi(acc.upi_id || "");
    }
    setTutorAccounts(null); // collapse picker
    toast.success(`Imported · ${acc.nickname}`);
  };

  // Phase 7.37g — Sharable hitch.zone onboarding URL when the tutor needs KYC
  // (or just hasn't added a bank yet). Partner taps "Send KYC link", we
  // construct the URL + WhatsApp deep-link; partner taps to share.
  const generateTutorKycLink = async (includeBank = true) => {
    if (!tutorMobile || tutorMobile.length !== 10) {
      toast.error("Enter a valid 10-digit tutor mobile first");
      return;
    }
    setKycLinkBusy(true);
    try {
      // Phase 7.37k — create a tracked KYC link instead of a one-off URL.
      // Partner sees opens / completions on /kyc-links page.
      const { data } = await api.post("/kyc-links", {
        mobile: tutorMobile,
        purpose: "tutor",
        partner_note: studentName ? `For student: ${studentName}` : undefined,
        include_bank: includeBank,
        expires_in_days: 30,
      });
      const url = `${window.location.origin}/onboard/${data.token}`;
      const text = [
        "Hi! To complete onboarding for PayFlex payments,",
        `please verify your KYC${includeBank ? " + add a bank account" : ""} via this secure link:`,
        url,
      ].join("\n");
      setKycLinkData({
        url,
        whatsapp_url: `https://wa.me/91${tutorMobile}?text=${encodeURIComponent(text)}`,
        sms_text: text,
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to generate link");
    } finally {
      setKycLinkBusy(false);
    }
  };

  const copyKycLink = async () => {
    if (!kycLinkData?.url) return;
    try {
      await navigator.clipboard.writeText(kycLinkData.url);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — long-press the URL instead");
    }
  };

  const allowedRails = subscription?.allowed_rails || ["upi", "imps", "neft", "rtgs"];

  // ────────── Step 1: send OTP ──────────
  const sendOtp = async () => {
    if (!/^[6-9][0-9]{9}$/.test(customerMobile)) return toast.error("Enter a valid 10-digit mobile");
    setBusy(true);
    try {
      const { data } = await api.post("/customer/send-otp", { customer_mobile: customerMobile, channel: otpChannel });
      // If a verified session already exists for (retailer × customer) within last 30 min,
      // backend auto-issues a fresh token — skip the OTP step entirely.
      if (data.reused_session && data.customer_otp_token) {
        setOtpToken(data.customer_otp_token);
        setReusedSession(true);
        const { data: bd } = await api.get(`/customer/${customerMobile}/beneficiaries`);
        setBeneficiaries(bd.items || []);
        toast.success("Customer already verified · skipping OTP");
        setStep(2);
        return;
      }
      setOtpSent(true);
      setDevOtp(data.dev_otp);
      setWhatsappUrl(data.whatsapp_url || "");
      if (data.whatsapp_url) {
        toast.success("WhatsApp link ready · share it with the customer");
      } else {
        toast.success(`OTP sent to ${customerMobile}${data.dev_otp ? ` (dev: ${data.dev_otp})` : ""}`);
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 412 && detail && typeof detail === "object" && detail.code === "customer_kyc_required") {
        setKycBlocked({ kyc_status: detail.kyc_status, message: detail.message, mobile: detail.customer_mobile });
        toast.error(`Customer KYC ${detail.kyc_status === "missing" ? "missing" : detail.kyc_status} — verify before sending OTP.`);
      } else {
        toast.error(formatApiError(detail) || "Failed to send OTP");
      }
    } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    if (otp.length < 4) return toast.error("Enter the 6-digit OTP");
    setBusy(true);
    try {
      const { data } = await api.post("/customer/verify-otp", { customer_mobile: customerMobile, otp });
      setOtpToken(data.customer_otp_token);
      // load this customer's beneficiaries
      const { data: bd } = await api.get(`/customer/${customerMobile}/beneficiaries`);
      setBeneficiaries(bd.items || []);
      toast.success("Customer verified");
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invalid OTP");
    } finally { setBusy(false); }
  };

  // ────────── Step 4: add bene inline ──────────
  // Verify the inline hitch.zone OTP entered in the Add-bene modal, then run
  // the actual bene-add. Used when the customer's hitch.zone session expired
  // mid-flow and we collected a fresh OTP within the same dialog.
  const verifyAndAddBene = async () => {
    if (!addBeneOtp || addBeneOtp.length < 4) return;
    setBusy(true);
    try {
      await api.post("/customer/hitch-otp/verify", {
        customer_mobile: customerMobile,
        otp: addBeneOtp,
      });
      // Session is now cached server-side — submit the actual bene add.
      await _submitBeneAdd();
      setAddBeneOpen(false);
      setAddBeneOtpStep("idle");
      setAddBeneOtp("");
      setBneNickname(""); setBneUpi(""); setBneAcc(""); setBneIfsc(""); setBneHolder("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invalid OTP — please retry");
    } finally { setBusy(false); }
  };


  const _submitBeneAdd = async () => {
    // Education mode: tutor must be hitch.zone KYC-verified.
    if (isEducation && (!tutorKyc || !tutorKyc.is_verified)) {
      toast.error("Tutor's hitch.zone KYC must be verified first. Click 'Check KYC' next to the tutor mobile.");
      return;
    }
    if (isSelf) {
      // Self-mode: regular endpoint, no OTP token, no customer linkage.
      await api.post("/beneficiaries", {
        type: bneType,
        nickname: bneNickname,
        upi_id: bneType === "upi" ? bneUpi : null,
        account_number: bneType === "bank" ? bneAcc : null,
        ifsc: bneType === "bank" ? bneIfsc.toUpperCase() : null,
        account_holder_name: bneType === "bank" ? bneHolder : null,
        tutor_mobile: isEducation ? tutorMobile : null,
      });
      const { data: bd } = await api.get(`/beneficiaries`);
      setBeneficiaries(bd.items || []);
      toast.success("Beneficiary added");
      return;
    }
    const { data: addResp } = await api.post("/beneficiaries/with-otp", {
      customer_mobile: customerMobile,
      customer_otp_token: otpToken,
      type: bneType,
      nickname: bneNickname,
      upi_id: bneType === "upi" ? bneUpi : null,
      account_number: bneType === "bank" ? bneAcc : null,
      ifsc: bneType === "bank" ? bneIfsc.toUpperCase() : null,
      account_holder_name: bneType === "bank" ? bneHolder : null,
      tutor_mobile: isEducation ? tutorMobile : null,
    });
    const { data: bd } = await api.get(`/customer/${customerMobile}/beneficiaries`);
    setBeneficiaries(bd.items || []);
    // If KYC name didn't match, the bene is pending — surface that clearly.
    const newStatus = addResp?.approval_status || "approved";
    if (newStatus.startsWith("pending_")) {
      toast.warning(
        "Added — awaiting admin approval (KYC name didn't match account holder). Bene cannot be used until L1→L2→L3 approval completes.",
        { duration: 8000 }
      );
    } else {
      toast.success("Beneficiary added");
    }
  };

  const addBeneficiary = async () => {
    setBusy(true);
    try {
      // For customer-mode bank benes the server requires a live hitch.zone
      // session (used to fetch the verified holder name). Check ahead so we
      // can prompt for an inline OTP instead of failing with a 401.
      if (!isSelf && bneType === "bank") {
        const { data: status } = await api.get(`/customer/hitch-otp/status/${customerMobile}`);
        if (!status.has_session) {
          // No live session — switch the dialog into "OTP needed" mode.
          await api.post("/customer/hitch-otp/send", { customer_mobile: customerMobile });
          toast.success("OTP sent — enter it to add this bank account");
          setAddBeneOtpStep("otp_required");
          return;
        }
      }
      await _submitBeneAdd();
      setAddBeneOpen(false);
      setAddBeneOtpStep("idle");
      setAddBeneOtp("");
      setBneNickname(""); setBneUpi(""); setBneAcc(""); setBneIfsc(""); setBneHolder("");
    } catch (err) {
      const code = err.response?.data?.detail?.code;
      if (code === "hitch_session_expired") {
        // Backend disagreed with our client-side check (rare race). Switch to
        // OTP step and start the round-trip.
        try {
          await api.post("/customer/hitch-otp/send", { customer_mobile: customerMobile });
          toast.success("OTP sent — enter it to continue");
          setAddBeneOtpStep("otp_required");
        } catch {
          toast.error("Could not start hitch.zone OTP — try again");
        }
        return;
      }
      if (code === "customer_pan_missing") {
        toast.warning("Customer's PAN is missing. Run 'Import saved accounts' to refresh KYC first.");
        setAddBeneOpen(false);
        setHitchImportOpen(true);
        return;
      }
      toast.error(formatApiError(err.response?.data?.detail) || "Could not add beneficiary");
    } finally { setBusy(false); }
  };

  // ────────── Import saved accounts via hitch.zone (parent system) ──────────
  const startHitchImport = async () => {
    setHitchImportBusy(true);
    try {
      // Quickly check whether a live hitch.zone session already exists. If yes,
      // skip the OTP round-trip and pull accounts directly via the merged
      // /customer/<mobile>/beneficiaries endpoint.
      const { data: status } = await api.get(`/customer/hitch-otp/status/${customerMobile}`);
      if (status.has_session) {
        const { data: bd } = await api.get(`/customer/${customerMobile}/beneficiaries`);
        setBeneficiaries(bd.items || []);
        const newCount = (bd.items || []).filter((b) => b.source === "hitch_zone" || b.source === "both").length;
        toast.success(`${newCount} parent-system account${newCount === 1 ? "" : "s"} imported`);
        setHitchImportOpen(false);
        return;
      }
      await api.post("/customer/hitch-otp/send", { customer_mobile: customerMobile });
      toast.success("OTP sent — check the customer's phone");
      setHitchImportStep("otp_sent");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start import");
    } finally { setHitchImportBusy(false); }
  };

  const completeHitchImport = async () => {
    if (!hitchOtp || hitchOtp.length < 4) return;
    setHitchImportBusy(true);
    try {
      // Phase 7.37m — Verify OTP triggers a fresh hitch.zone login + immediate
      // list_accounts on the server. Response includes accounts_preview so the
      // frontend doesn't need a second round-trip.
      const { data } = await api.post("/customer/hitch-otp/verify", {
        customer_mobile: customerMobile, otp: hitchOtp,
      });
      const preview = data?.accounts_preview || {};
      const kycMsg = data?.parent_kyc?.auto_approved ? " · KYC auto-verified" : "";
      // Re-list to get the merged (local + hitch) view with the freshly cached token
      const { data: bd } = await api.get(`/customer/${customerMobile}/beneficiaries`);
      setBeneficiaries(bd.items || []);
      const meta = bd.hitch || {};
      const importedFromMerged = meta.imported ?? (bd.items || []).filter((b) => b.source === "hitch_zone" || b.source === "both").length;
      // Prefer the preview count from the verify response — it's the direct
      // hitch.zone result, untainted by local dedup. If merged path returns 0
      // but preview returned N, the accounts are local duplicates with parent
      // status overlaid (still successful).
      const previewCount = preview.count || 0;
      if (previewCount > 0 || importedFromMerged > 0) {
        toast.success(`${Math.max(previewCount, importedFromMerged)} saved account${(previewCount || importedFromMerged) === 1 ? "" : "s"} imported${kycMsg}`);
      } else if (preview.error) {
        toast.warning(`hitch.zone returned an error: ${preview.error}${kycMsg}`);
      } else if (meta.error) {
        toast.warning(`No accounts imported: ${meta.error}${kycMsg}`);
      } else {
        toast.info(`OTP verified${kycMsg} · No saved accounts at hitch.zone for this customer yet.`);
      }
      setHitchImportOpen(false);
      setHitchOtp("");
      setHitchImportStep("idle");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invalid OTP");
    } finally { setHitchImportBusy(false); }
  };

  // ────────── Delete beneficiary (soft-delete) ──────────
  const deleteBeneficiary = async (b) => {
    const label = b.type === "upi" ? b.upi_id : `${b.account_holder_name} · ${b.account_number?.slice(-4)}`;
    if (!window.confirm(`Remove "${b.nickname}" (${label})? You can re-add it later if needed.`)) return;
    setBusy(true);
    try {
      await api.delete(`/beneficiaries/${b.id}`);
      // Refresh list (self vs customer-scoped)
      if (isSelf) {
        const { data: bd } = await api.get(`/beneficiaries`);
        setBeneficiaries(bd.items || []);
      } else {
        const { data: bd } = await api.get(`/customer/${customerMobile}/beneficiaries`);
        setBeneficiaries(bd.items || []);
      }
      // Clear selection if the deleted one was selected
      setBeneId((prev) => (prev === b.id ? "" : prev));
      toast.success("Beneficiary removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete beneficiary");
    } finally { setBusy(false); }
  };

  // ────────── Step 5: SDK callers ──────────
  const payWithRazorpay = async ({ transaction, sdk_params }) => {
    await loadScript(gwConfig.razorpay.sdk_url);
    return new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        ...sdk_params,
        handler: async (res) => {
          try {
            const r = await api.post("/payouts/verify/razorpay", {
              reference: transaction.id, razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature,
            });
            resolve(r.data);
          } catch (err) { reject(err); }
        },
        modal: { ondismiss: () => reject(new Error("Payment window closed")) },
      });
      rzp.on("payment.failed", (resp) => reject(new Error(resp.error?.description || "Payment failed")));
      rzp.open();
    });
  };
  const payWithCashfree = async ({ transaction, sdk_params }) => {
    await loadScript(gwConfig.cashfree.sdk_url);
    const cf = window.Cashfree({ mode: sdk_params.mode });
    const result = await cf.checkout({ paymentSessionId: sdk_params.payment_session_id, redirectTarget: "_modal" });
    if (result?.error) throw new Error(result.error.message || "Payment error");
    const { data } = await api.post("/payouts/verify/cashfree", { reference: transaction.id });
    return data;
  };

  const initiatePayout = async () => {
    if (!isSelf && gateway !== "customer_wallet" && !gwConfig?.[gateway]?.enabled) return toast.error(`${gateway} is not configured`);
    if (isEducation && !eduCategory) return toast.error("Select an education category");
    setBusy(true);
    trackEvent("payout_initiated", { rail, amount: parseFloat(amount) || 0, product: isEducation ? "education" : "payout" });
    try {
      const payload = {
        customer_mobile: customerMobile,
        beneficiary_id: beneId, rail, amount: parseFloat(amount),
        payment_method: paymentMethod,
        gateway: isSelf ? "partner_wallet" : gateway,
        is_self: isSelf,
      };
      if (!isSelf) {
        payload.customer_otp_token = otpToken;
      }
      if (isEducation) {
        payload.product = "education";
        payload.education_category = eduCategory;
        if (studentName) payload.student_name = studentName;
        if (studentClass) payload.student_class = studentClass;
        if (studentRoll) payload.student_roll = studentRoll;
      }
      const { data } = await api.post("/payouts/v2/initiate", payload);
      // Attribution — tie payout back to original ad campaign (if any).
      try {
        const { reportLandingConversion } = await import("@/lib/landingAttribution");
        reportLandingConversion(
          data?.transaction?.id || data?.transaction?.display_id,
          data?.transaction?.collected_amount || data?.transaction?.amount || 0,
          "payout",
        );
      } catch { /* never block success UX */ }
      trackEvent("payout_completed", {
        amount: data?.transaction?.collected_amount || data?.transaction?.amount || 0,
        rail,
        txn_id: data?.transaction?.display_id || "",
      });
      if (isSelf) {
        toast.success("Self payout submitted · debited from your wallet · awaits L1 approval");
        refreshUser();
        setTimeout(() => navigate(`/transactions`), 600);
      } else if (paymentMethod === "link") {
        setLinkUrl(data.payment_link_url); setLinkOpen(true);
        toast.success("Payment link created · share with the customer");
      } else if (gateway === "customer_wallet") {
        toast.success("Customer wallet debited · payout now awaits L1 approval");
        refreshUser();
        setTimeout(() => navigate(`/payouts/pending-collections`), 600);
      } else {
        const result = gateway === "razorpay"
          ? await payWithRazorpay({ transaction: data.transaction, sdk_params: data.sdk_params })
          : await payWithCashfree({ transaction: data.transaction, sdk_params: data.sdk_params });
        toast.success("Collection successful · payout now awaits L1 approval");
        refreshUser();
        setTimeout(() => navigate(`/payouts/pending-collections`), 600);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Could not initiate payout");
    } finally { setBusy(false); }
  };

  // Phase 7.37s — Shared helper used by both the tutor (Education) and the
  // customer (KYC-blocked) flows. Creates a KYC link, copies the public URL,
  // opens WhatsApp pre-filled. Single source of truth so the two CTAs stay
  // in sync.
  const createAndShareKycLink = async ({ mobile, purpose, note }) => {
    try {
      const { data } = await api.post("/kyc-links", {
        mobile, purpose,
        partner_note: note || `KYC for +91 ${mobile}`,
        include_bank: true,
        expires_in_days: 30,
      });
      const onboardUrl = `${window.location.origin}/onboard/${data.token}`;
      const waText = encodeURIComponent(`Hi! Please complete KYC for FlexPay here: ${onboardUrl}`);
      try { await navigator.clipboard.writeText(onboardUrl); } catch { /* noop */ }
      window.open(`https://wa.me/91${mobile}?text=${waText}`, "_blank");
      toast.success("KYC link created · copied to clipboard · WhatsApp opened");
      return data;
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't create KYC link"));
    }
  };

  // Phase 7.37s — when picking a bene, auto-flip rail if it's on the other
  // type (bank vs UPI). Saves the user from a manual rail toggle.
  const pickBene = (b) => {
    setBeneId(b.id);
    const needsBank = b.type === "bank";
    const needsUpi = b.type === "upi";
    const currentRail = PAYOUT_RAILS.find((rl) => rl.id === rail);
    const matches = currentRail?.beneficiary_type === b.type;
    if (matches) return;
    if (needsUpi && allowedRails.includes("upi")) setRail("upi");
    else if (needsBank) {
      const bankPref = ["imps", "neft", "rtgs"].find((r) => allowedRails.includes(r));
      if (bankPref) setRail(bankPref);
    }
  };

  const eligibleBenes = beneficiaries;

  // Tally to surface "2 bank · 1 UPI" hint above the list.
  const otherRailCounts = {
    bank: beneficiaries.filter((b) => b.type === "bank").length,
    upi: beneficiaries.filter((b) => b.type === "upi").length,
  };
  const currentRailBeneType = PAYOUT_RAILS.find((rl) => rl.id === rail)?.beneficiary_type;
  const hiddenOnOtherType = 0; // no longer hidden — kept for compat below.

  const collectedTotal = (parseFloat(amount) || 0) + pgFee;
  const railBlocked = !allowedRails.includes(rail);

  return (
    <DashboardLayout>
      <div className="animate-fade-up">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <span className="overline">{isEducation ? "Education Fee Wizard" : "Payout Wizard"}</span>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
              {isEducation ? (
                <span className="inline-flex items-center gap-2">
                  <GraduationCap className="h-7 w-7" /> Pay an education fee
                </span>
              ) : "Send funds in 5 steps"}
            </h1>
            <p className="text-muted-foreground mt-2 max-w-xl">
              {isEducation
                ? "Customer (student / parent) pays the fee → it lands in the tutor or institution's bank account. Uses the same payout rails (UPI / IMPS / NEFT / RTGS) and 3-level approval pipeline."
                : "Mobile-first payout — verify the customer, pick the amount + rail, choose a beneficiary, and collect via Razorpay/Cashfree."}
            </p>
          </div>
          <Link to="/payouts/pending-collections">
            <Button variant="outline" data-testid="view-pending"><AlertCircle className="h-4 w-4 mr-1" /> Pending links</Button>
          </Link>
        </div>

        <Stepper step={step} />

        {/* Phase 7.37h — Top-of-page Pay For toggle (default = Self transaction)
            Phase 7.37l — Locked after step 1 to prevent mid-flow swap */}
        <div className="mt-5 rounded-md border border-border bg-card px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="payout-mode-toggle-top">
          <div className="flex items-center gap-3">
            <span className="overline text-[10px]">Pay for</span>
            <div className="inline-flex rounded-md border bg-background p-1" role="tablist">
              <button
                type="button"
                onClick={() => { setIsSelf(false); setCustomerMobile(""); setOtpToken(""); setOtpSent(false); }}
                disabled={otpSent || step > 1}
                data-testid="payout-mode-customer-top"
                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${!isSelf ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"} ${(otpSent || step > 1) ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                Customer
              </button>
              <button
                type="button"
                onClick={() => { setIsSelf(true); }}
                disabled={otpSent || step > 1}
                data-testid="payout-mode-self-top"
                className={`px-3 py-1 text-xs font-medium rounded transition-colors inline-flex items-center gap-1 ${isSelf ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"} ${(otpSent || step > 1) ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <UserCircle2 className="h-3 w-3" /> Self transaction
              </button>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground max-w-md text-right">
            {step > 1
              ? "Mode locked — go back to step 1 to switch."
              : isSelf
                ? "Debiting your partner wallet — no customer OTP required."
                : "Customer authenticates via OTP and pays via gateway."}
          </p>
        </div>

        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 surface-card p-6">
            {/* Customer wallet card — visible from step 2 onwards (after OTP) in customer mode */}
            {step > 1 && !isSelf && customerMobile && otpToken && (
              <div className="mb-6">
                <CustomerWalletPanel
                  mobile={customerMobile}
                  testidPrefix="payout-cw"
                />
              </div>
            )}
            {/* Step 1: Customer mobile + OTP */}
            {step === 1 && (
              <section data-testid="step-1">
                <h3 className="font-display text-xl font-semibold">1 · {isSelf ? "Self payout" : "Customer mobile"}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {isSelf
                    ? "Pay yourself · we'll debit YOUR partner wallet. No customer KYC or OTP needed."
                    : "We'll send an OTP via SMS or WhatsApp to authorise this payout. Verified sessions stay valid for 30 minutes — no re-OTP within that window."}
                </p>

                {kycBlocked && !isSelf && (
                  <div className="mt-4 rounded-md border border-warning/40 bg-warning/5 p-4 text-sm" data-testid="payout-kyc-banner">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <div className="font-medium">Customer KYC required</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Status: <Badge variant="outline" className="ml-1 border-warning/40 text-warning">{kycBlocked.kyc_status || "missing"}</Badge>
                          <span className="ml-2">{kycBlocked.message || "Complete the customer's KYC before sending OTP."}</span>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => navigate(`/customer-kyc?mobile=${kycBlocked.mobile}&return=/payouts`)}
                            data-testid="payout-kyc-go"
                          >
                            <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Verify in PayFlex
                          </Button>
                          {/* Phase 7.37s — alternate flow: send the customer a public KYC link */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => createAndShareKycLink({
                              mobile: kycBlocked.mobile,
                              purpose: "customer",
                              note: `Customer KYC for payout`,
                            })}
                            data-testid="payout-kyc-share-link"
                          >
                            <Link2 className="h-3.5 w-3.5 mr-1" /> Share KYC link on WhatsApp
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Phase 7.18 — Self vs Customer mode toggle */}
                <div className="mt-5">
                  <Label>Pay for</Label>
                  <div className="inline-flex rounded-md border bg-background p-1 mt-1" role="tablist" data-testid="payout-mode-toggle">
                    <button
                      type="button"
                      onClick={() => setIsSelf(false)}
                      disabled={otpSent}
                      data-testid="payout-mode-customer"
                      className={`px-4 py-1.5 text-sm font-medium rounded transition-colors ${!isSelf ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"} ${otpSent ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      Customer
                    </button>
                    <button
                      type="button"
                      onClick={() => { setIsSelf(true); }}
                      disabled={otpSent}
                      data-testid="payout-mode-self"
                      className={`px-4 py-1.5 text-sm font-medium rounded transition-colors ${isSelf ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"} ${otpSent ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      Self transaction
                    </button>
                  </div>
                </div>

                {!isSelf && (<>
                <div className="mt-5">
                  <Label>Send OTP via</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1 max-w-sm">
                    <button type="button" onClick={() => setOtpChannel("sms")} disabled={otpSent}
                      data-testid="otp-channel-sms"
                      className={`rounded-md border px-3 py-2.5 text-sm font-medium transition-all flex items-center justify-center gap-2 ${otpChannel === "sms" ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-accent"} ${otpSent ? "opacity-60 cursor-not-allowed" : ""}`}>
                      <Smartphone className="h-4 w-4" /> SMS
                    </button>
                    <button type="button" onClick={() => setOtpChannel("whatsapp")} disabled={otpSent}
                      data-testid="otp-channel-whatsapp"
                      className={`rounded-md border px-3 py-2.5 text-sm font-medium transition-all flex items-center justify-center gap-2 ${otpChannel === "whatsapp" ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-accent"} ${otpSent ? "opacity-60 cursor-not-allowed" : ""}`}>
                      <MessageCircle className="h-4 w-4" /> WhatsApp
                    </button>
                  </div>
                </div>
                </>)}

                {isSelf && (
                  <div className="mt-6">
                    <Button
                      onClick={async () => {
                        const myMobile = (user?.mobile || "").trim();
                        if (!myMobile) {
                          toast.error("Your mobile number is missing on the account.");
                          return;
                        }
                        setCustomerMobile(myMobile);
                        setOtpToken("");
                        try {
                          // Self-mode loads all partner-owned beneficiaries (no customer scoping).
                          const { data: bd } = await api.get(`/beneficiaries`);
                          setBeneficiaries(bd.items || []);
                        } catch (err) {
                          setBeneficiaries([]);
                        }
                        setStep(2);
                      }}
                      data-testid="payout-self-continue"
                      size="lg"
                    >
                      Continue to amount →
                    </Button>
                  </div>
                )}

                {!isSelf && (<>
                <div className="mt-5 grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Mobile number</Label>
                    <Input data-testid="customer-mobile" inputMode="numeric" maxLength={10}
                      value={customerMobile} onChange={(e) => setCustomerMobile(e.target.value.replace(/\D/g, ""))}
                      disabled={otpSent} placeholder="98xxxxxxxx" className="mt-1 font-mono" />
                  </div>
                  {!otpSent ? (
                    <Button onClick={sendOtp} disabled={busy || customerMobile.length !== 10} data-testid="send-otp" className="self-end">
                      {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : (otpChannel === "whatsapp" ? <MessageCircle className="h-4 w-4 mr-1" /> : <Smartphone className="h-4 w-4 mr-1" />)}
                      Send OTP via {otpChannel === "whatsapp" ? "WhatsApp" : "SMS"}
                    </Button>
                  ) : (
                    <Button onClick={() => { setOtpSent(false); setOtp(""); setDevOtp(null); setWhatsappUrl(""); }} variant="outline" data-testid="resend-otp" className="self-end">
                      Edit number
                    </Button>
                  )}
                </div>

                {otpSent && whatsappUrl && (
                  <div className="mt-4 rounded-md border border-success/40 bg-success/5 p-3 text-sm" data-testid="whatsapp-share">
                    <div className="flex items-start gap-2">
                      <MessageCircle className="h-4 w-4 text-success mt-0.5" />
                      <div className="flex-1">
                        <div className="font-medium text-success">Share OTP via WhatsApp</div>
                        <div className="text-xs text-muted-foreground mt-1">Tap below to open WhatsApp with the OTP message pre-filled.</div>
                        <div className="mt-2 flex gap-2 flex-wrap">
                          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" data-testid="open-whatsapp-link"><ExternalLink className="h-3.5 w-3.5 mr-1" /> Open WhatsApp</Button>
                          </a>
                          <Button size="sm" variant="outline" data-testid="copy-whatsapp-link" onClick={() => { navigator.clipboard?.writeText(whatsappUrl); toast.success("Link copied"); }}>
                            <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {otpSent && (
                  <div className="mt-5 grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Enter OTP</Label>
                      <Input data-testid="otp-input" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                        className="mt-1 font-mono tracking-widest text-lg" placeholder="000000" />
                      {devOtp && <p className="text-[11px] text-warning mt-1">Dev mode OTP: <code className="font-mono">{devOtp}</code></p>}
                    </div>
                    <Button onClick={verifyOtp} disabled={busy || otp.length < 4} data-testid="verify-otp" className="self-end">
                      {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />}
                      Verify & continue
                    </Button>
                  </div>
                )}
                </>)}
              </section>
            )}

            {/* Step 3: Amount + Rail + inline Pay mode */}
            {step === 3 && (
              <section data-testid="step-2">
                <BackButton onClick={() => setStep(2)} />
                <h3 className="font-display text-xl font-semibold">3 · {isEducation ? "Fee amount" : "Amount, rail & pay"}</h3>
                <p className="text-sm text-muted-foreground mt-1">PG fee is computed from your subscription ({subscription?.name}) slabs. Lock the amount to reveal pay mode.</p>

                {isEducation && (
                  <>
                    <div className="mt-5">
                      <Label>Education category</Label>
                      <Select value={eduCategory} onValueChange={setEduCategory} disabled={amountLocked}>
                        <SelectTrigger className="mt-1" data-testid="education-category-select">
                          <SelectValue placeholder="Pick the fee type" />
                        </SelectTrigger>
                        <SelectContent>
                          {eduCategories.length === 0 ? (
                            <div className="px-3 py-4 text-xs text-muted-foreground">
                              No categories enabled by admin yet.
                            </div>
                          ) : eduCategories.map((c) => (
                            <SelectItem key={c.id} value={c.id} data-testid={`edu-cat-${c.id}`}>
                              {c.label}{c.max_amount ? ` · max ₹${Number(c.max_amount).toLocaleString()}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                      <div>
                        <Label className="text-xs">Student name (optional)</Label>
                        <Input value={studentName} onChange={(e) => setStudentName(e.target.value)} disabled={amountLocked} className="mt-1" placeholder="e.g. Aisha Sharma" data-testid="student-name-input" />
                      </div>
                      <div>
                        <Label className="text-xs">Class / year (optional)</Label>
                        <Input value={studentClass} onChange={(e) => setStudentClass(e.target.value)} disabled={amountLocked} className="mt-1" placeholder="e.g. Class 10" data-testid="student-class-input" />
                      </div>
                      <div>
                        <Label className="text-xs">Roll / admission no. (optional)</Label>
                        <Input value={studentRoll} onChange={(e) => setStudentRoll(e.target.value)} disabled={amountLocked} className="mt-1" placeholder="e.g. R-4521" data-testid="student-roll-input" />
                      </div>
                    </div>
                  </>
                )}

                <div className="mt-5">
                  <Label>{isEducation ? "Fee amount (₹)" : "Amount to transfer (₹)"}</Label>
                  <Input data-testid="amount-input" type="number" min="1" step="any" value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    readOnly={amountLocked}
                    className={`mt-1 font-mono text-2xl ${amountLocked ? "bg-muted/40 cursor-not-allowed" : ""}`}
                    placeholder="0" />
                </div>
                <div className="mt-5">
                  <Label>Rail</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                    {PAYOUT_RAILS.map((r) => {
                      const allowed = allowedRails.includes(r.id);
                      const disabled = !allowed || amountLocked;
                      return (
                        <button type="button" key={r.id} disabled={disabled} onClick={() => setRail(r.id)}
                          data-testid={`rail-${r.id}`}
                          className={`rounded-md border px-3 py-3 text-left transition-all ${
                            rail === r.id && allowed ? "border-primary bg-primary/10"
                            : allowed && !amountLocked ? "border-border hover:bg-accent"
                            : "border-border bg-muted/40 text-muted-foreground cursor-not-allowed"
                          }`}>
                          <div className="text-xs font-bold uppercase">{r.id}</div>
                          <div className="text-[10px] text-muted-foreground mt-0.5">₹{r.min}–{r.max.toLocaleString()}</div>
                          {!allowed && <div className="text-[9px] text-warning mt-0.5">Upgrade plan</div>}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <Button className="mt-6" disabled={amountLocked || !amount || railBlocked || (isEducation && !eduCategory)} onClick={() => setAmountLocked(true)} data-testid="lock-amount">
                  {amountLocked ? (<><CheckCircle2 className="h-4 w-4 mr-1" /> Amount locked</>) : (<>Lock amount <ChevronRight className="h-4 w-4 ml-1" /></>)}
                </Button>
              </section>
            )}

            {/* Step 2: Beneficiary */}
            {step === 2 && (
              <section data-testid="step-3">
                <BackButton onClick={() => setStep(1)} />
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-display text-xl font-semibold">2 · {isEducation ? "Choose tutor / institution" : "Choose beneficiary"}</h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    {!isEducation && (
                      <Button size="sm" variant="outline" onClick={() => {
                        // Phase 7.37m — User-requested hard-refresh: every click
                        // forces a fresh hitch.zone OTP login + list_accounts call
                        // so we never serve stale cached state.
                        if (!customerMobile || customerMobile.length !== 10) {
                          toast.error("Customer mobile missing");
                          return;
                        }
                        setHitchImportOpen(true);
                      }} data-testid="hitch-import-btn">
                        <Download className="h-4 w-4 mr-1" /> Import saved accounts
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setAddBeneOpen(true)} data-testid="add-bene-inline">
                      <Plus className="h-4 w-4 mr-1" /> {isEducation ? "Add tutor" : "Add new"}
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Beneficiaries linked to <span className="font-mono">+91 {customerMobile}</span> · {eligibleBenes.length} account{eligibleBenes.length === 1 ? "" : "s"}
                  {otherRailCounts.bank > 0 && otherRailCounts.upi > 0 && (
                    <span className="ml-1 text-[11px]">
                      ({otherRailCounts.bank} bank · {otherRailCounts.upi} UPI)
                    </span>
                  )}
                </p>
                {eligibleBenes.length === 0 ? (
                  <div className="mt-5 rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                    {isEducation
                      ? "No tutors saved yet. Click \"Add tutor\" above to onboard one."
                      : `No beneficiaries for this customer yet. Click "Add new" above.`}
                  </div>
                ) : isEducation ? (
                  <div className="mt-5 space-y-4" data-testid="tutor-grouped-list">
                    {(() => {
                      // Phase 7.37j — Group education-fee benes by tutor identity
                      // (tutor_mobile + KYC name). Each tutor card lists their
                      // saved bank/UPI accounts. Avoids the flat list of nicknames
                      // shown for regular payouts where many accounts may belong
                      // to the same person.
                      const groups = new Map();
                      for (const b of eligibleBenes) {
                        const mob = (b.tutor_mobile || "—").trim() || "—";
                        if (!groups.has(mob)) groups.set(mob, { mobile: mob, name: "", items: [] });
                        const g = groups.get(mob);
                        g.items.push(b);
                        // Pick first holder-name we see as the tutor's display name
                        if (!g.name && b.account_holder_name) g.name = b.account_holder_name;
                      }
                      const tutors = Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name || ""));
                      return tutors.map((t) => (
                        <div
                          key={t.mobile}
                          className="rounded-md border border-border overflow-hidden"
                          data-testid={`tutor-group-${t.mobile}`}
                        >
                          <div className="bg-muted/50 px-3 py-2 border-b border-border flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold truncate">{t.name || "Unknown tutor"}</div>
                              <div className="text-[11px] text-muted-foreground font-mono">
                                {t.mobile === "—" ? "Legacy bene · no tutor mobile" : `+91 ${t.mobile}`}
                              </div>
                            </div>
                            <Badge variant="outline" className="text-[10px] shrink-0">
                              {t.items.length} account{t.items.length === 1 ? "" : "s"}
                            </Badge>
                          </div>
                          <div className="divide-y divide-border">
                            {t.items.map((b) => {
                              const approvalStatus = b.approval_status || "approved";
                              const isApproved = approvalStatus === "approved";
                              const isRejected = approvalStatus === "rejected";
                              const isPending = approvalStatus.startsWith("pending_");
                              const statusLabel = isPending
                                ? `Pending ${approvalStatus.split("_")[1]?.toUpperCase() || ""}`
                                : isRejected ? "Rejected" : null;
                              return (
                                <div key={b.id} className={`group relative w-full transition-all ${
                                  beneId === b.id ? "bg-primary/10"
                                  : !isApproved ? "opacity-70"
                                  : "hover:bg-accent"
                                }`}>
                                  <button
                                    type="button"
                                    onClick={() => isApproved && pickBene(b)}
                                    disabled={!isApproved}
                                    data-testid={`tutor-acc-${b.id}`}
                                    className="w-full text-left p-3 pr-10 disabled:cursor-not-allowed"
                                  >
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                      <div className="min-w-0">
                                        <div className="text-xs font-medium flex items-center gap-2 flex-wrap">
                                          <span>{b.nickname}</span>
                                          <Badge variant="outline" className="text-[9px] uppercase shrink-0">{b.type}</Badge>
                                          {b.hitch_verified_status && (
                                            <Badge
                                              variant="outline"
                                              className={`text-[9px] uppercase shrink-0 ${
                                                (b.hitch_verified_status || "").toLowerCase() === "active"
                                                  ? "border-emerald-400 text-emerald-700 bg-emerald-50"
                                                  : "border-amber-400 text-amber-800 bg-amber-50"
                                              }`}
                                            >
                                              Parent · {b.hitch_verified_status}
                                            </Badge>
                                          )}
                                          {statusLabel && (
                                            <Badge variant="outline" className={`text-[9px] ${isRejected ? "border-destructive/40 text-destructive bg-destructive/10" : "border-warning/40 text-warning bg-warning/10"}`}>
                                              {statusLabel}
                                            </Badge>
                                          )}
                                        </div>
                                        <div className="text-[11px] text-muted-foreground font-mono mt-0.5 truncate">
                                          {b.type === "upi"
                                            ? b.upi_id
                                            : `${b.account_number?.slice(-4) ? "****" + b.account_number.slice(-4) : b.account_number} · ${b.ifsc}`}
                                        </div>
                                        {isRejected && b.rejection_reason && (
                                          <div className="text-[10px] text-destructive mt-0.5 italic">Reason: {b.rejection_reason}</div>
                                        )}
                                      </div>
                                    </div>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); deleteBeneficiary(b); }}
                                    disabled={busy}
                                    data-testid={`tutor-acc-delete-${b.id}`}
                                    title="Remove account"
                                    className="absolute top-1/2 -translate-y-1/2 right-2 p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                ) : (
                  <div className="mt-5 space-y-2">
                    {eligibleBenes.map((b) => {
                      const approvalStatus = b.approval_status || "approved";
                      const isHitchOnly = b.source === "hitch_zone" && !b.id;
                      const isApproved = approvalStatus === "approved" && !isHitchOnly;
                      const isRejected = approvalStatus === "rejected";
                      const isPending = approvalStatus.startsWith("pending_");
                      const statusLabel = isHitchOnly
                        ? "From parent system — add to use"
                        : isPending
                          ? `Pending ${approvalStatus.split("_")[1]?.toUpperCase() || ""} approval`
                          : isRejected ? "Rejected" : null;
                      return (
                        <div key={b.id || `hitch-${b.account_number}-${b.ifsc}`} className={`group relative w-full rounded-md border transition-all ${
                          beneId === b.id ? "border-primary bg-primary/10" :
                          !isApproved ? "border-border opacity-70" :
                          "border-border hover:bg-accent"
                        }`}>
                          <button
                            type="button"
                            onClick={() => {
                              if (isApproved) return pickBene(b);
                              if (isHitchOnly) {
                                // Pre-fill the Add modal with the parent-system account
                                setBneType("bank");
                                setBneNickname(b.nickname || b.account_holder_name?.slice(0, 30) || "");
                                setBneAcc(b.account_number || "");
                                setBneIfsc((b.ifsc || "").toUpperCase());
                                setBneHolder(b.account_holder_name || "");
                                setAddBeneOpen(true);
                              }
                            }}
                            disabled={!isApproved && !isHitchOnly}
                            data-testid={`bene-${b.id || `hitch-${b.account_number}`}`}
                            className="w-full text-left p-4 pr-12 disabled:cursor-not-allowed"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <div className="font-medium flex items-center gap-2 flex-wrap">
                                <span>{b.nickname}</span>
                                {/* Parent-system (hitch.zone) raw status — e.g. active / pending */}
                                {b.hitch_verified_status && (
                                  <Badge
                                    variant="outline"
                                    className={`text-[10px] uppercase ${
                                      (b.hitch_verified_status || "").toLowerCase() === "active"
                                        ? "border-emerald-400 text-emerald-700 bg-emerald-50"
                                        : "border-amber-400 text-amber-800 bg-amber-50"
                                    }`}
                                    data-testid={`bene-hitch-status-${b.id || `hitch-${b.account_number}`}`}
                                  >
                                    Parent · {b.hitch_verified_status}
                                  </Badge>
                                )}
                                {(isHitchOnly || !isApproved) && (
                                  <Badge
                                    variant="outline"
                                    className={isRejected
                                      ? "border-destructive/40 text-destructive bg-destructive/10 text-[10px]"
                                      : isHitchOnly
                                        ? "border-primary/40 text-primary bg-primary/10 text-[10px]"
                                        : "border-warning/40 text-warning bg-warning/10 text-[10px]"}
                                    data-testid={`bene-status-${b.id || `hitch-${b.account_number}`}`}
                                  >
                                    {statusLabel}
                                  </Badge>
                                )}
                              </div>
                                <div className="text-xs text-muted-foreground font-mono mt-0.5">
                                  {b.type === "upi" ? b.upi_id : `${b.account_holder_name} · ${b.account_number?.slice(-4)} · ${b.ifsc}`}
                                </div>
                                {isRejected && b.rejection_reason && (
                                  <div className="text-[10px] text-destructive mt-1 italic">
                                    Reason: {b.rejection_reason}
                                  </div>
                                )}
                              </div>
                              <Badge variant="outline" className="text-[10px] mr-2 shrink-0">{b.type}</Badge>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); deleteBeneficiary(b); }}
                            disabled={busy}
                            data-testid={`bene-delete-${b.id}`}
                            title="Remove beneficiary"
                            className="absolute top-1/2 -translate-y-1/2 right-3 p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                <Button className="mt-6" disabled={!beneId} onClick={() => setStep(3)} data-testid="step3-next">
                  Continue <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </section>
            )}

            {/* Inline Pay mode (revealed after amount lock on Step 3) */}
            {step === 3 && amountLocked && (
              <section data-testid="step-4" className="mt-8 pt-6 border-t border-border">
                <button
                  type="button"
                  onClick={() => setAmountLocked(false)}
                  className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3"
                  data-testid="unlock-amount"
                >
                  <ArrowLeft className="h-3 w-3" /> Unlock amount
                </button>
                {isSelf ? (
                  <>
                    <h3 className="font-display text-xl font-semibold">4 · Pay from your wallet</h3>
                    <div className="mt-4 rounded-md border border-primary/40 bg-primary/5 p-4 text-sm" data-testid="payout-self-confirm">
                      <div className="font-semibold mb-1">Self payout · partner wallet</div>
                      <div className="text-xs text-muted-foreground">
                        We will debit <span className="font-mono font-semibold">{formatINR(collectedTotal)}</span> from
                        your partner wallet immediately. The payout enters L1 → L2 → L3 admin
                        approval; on L3 success the beneficiary is paid by the provider.
                      </div>
                    </div>
                    <Button className="mt-8" disabled={busy || !beneId} onClick={initiatePayout} data-testid="confirm-pay">
                      {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                      Pay {formatINR(collectedTotal)} from MY wallet
                    </Button>
                  </>
                ) : (
                  <>
                <h3 className="font-display text-xl font-semibold">4 · Payment gateway</h3>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {["razorpay", "cashfree", "customer_wallet"].map((gw) => {
                    const enabled = gw === "customer_wallet" ? true : gwConfig?.[gw]?.enabled;
                    const labels = { razorpay: "Razorpay", cashfree: "Cashfree", customer_wallet: "Customer Wallet" };
                    return (
                      <button type="button" key={gw} disabled={!enabled} onClick={() => setGateway(gw)}
                        data-testid={`gw-${gw}`}
                        className={`rounded-md border px-3 py-2 text-sm font-medium ${gateway === gw && enabled ? "border-primary bg-primary/10 text-primary" : enabled ? "border-border hover:bg-accent" : "border-border bg-muted/40 text-muted-foreground cursor-not-allowed"}`}>
                        {labels[gw]}
                        {!enabled && <div className="text-[10px] opacity-70">not configured</div>}
                        {gw === "customer_wallet" && <div className="text-[10px] opacity-70">debit customer's wallet</div>}
                      </button>
                    );
                  })}
                </div>

                <h3 className="font-display text-xl font-semibold mt-8">5 · Collection method</h3>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => setPaymentMethod("in_shop")} data-testid="pm-in-shop"
                    className={`rounded-md border p-4 text-left ${paymentMethod === "in_shop" ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`}>
                    <div className="font-semibold text-sm">Pay here (in-shop)</div>
                    <div className="text-[11px] text-muted-foreground mt-1">Customer pays from your device — popup checkout.</div>
                    <div className="text-[10px] font-mono mt-2 text-muted-foreground">Slab fee: {formatINR(subscription?.rail_slabs?.[rail]?.find((s) => parseFloat(amount) >= s.min && parseFloat(amount) <= s.max)?.in_shop_fee || 0)}</div>
                  </button>
                  <button type="button" onClick={() => setPaymentMethod("link")} data-testid="pm-link"
                    className={`rounded-md border p-4 text-left ${paymentMethod === "link" ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`}>
                    <div className="font-semibold text-sm">Create link</div>
                    <div className="text-[11px] text-muted-foreground mt-1">Generate a shareable URL via SMS/WhatsApp.</div>
                    <div className="text-[10px] font-mono mt-2 text-muted-foreground">Slab fee: {formatINR(subscription?.rail_slabs?.[rail]?.find((s) => parseFloat(amount) >= s.min && parseFloat(amount) <= s.max)?.link_fee || 0)}</div>
                  </button>
                </div>
                <div className="mt-3 text-[11px] text-muted-foreground">
                  Total PG fee shown in the right panel = slab fee + rail flat (₹{(subscription?.rail_fees?.[rail]?.find((s) => parseFloat(amount) >= s.min && parseFloat(amount) <= s.max)?.fee) || 0}) + {subscription?.pg_fee_percent || 0}% of amount + ₹{subscription?.otp_fee || 0} OTP fee.
                </div>

                <Button className="mt-8" disabled={busy} onClick={initiatePayout} data-testid="confirm-pay">
                  {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                  {paymentMethod === "link" ? `Create payment link for ${formatINR(collectedTotal)}` : `Collect ${formatINR(collectedTotal)}`}
                </Button>
                  </>
                )}
              </section>
            )}
          </div>

          <aside className="surface-card p-6 h-fit sticky top-6">
            <span className="overline">Summary</span>
            <h3 className="font-display text-xl font-bold tracking-tight mt-1">Payment preview</h3>
            <div className="mt-5 space-y-3 text-sm">
              <Row label="Subscription" value={<Badge variant="outline">{subscription?.name || "—"}</Badge>} />
              <Row label="Customer" value={customerMobile ? <span className="inline-flex items-center gap-1.5">+91 {customerMobile}{reusedSession && <Badge variant="outline" className="text-[9px] py-0 h-4 border-success/50 text-success">Verified · 30m</Badge>}</span> : <em className="text-muted-foreground">—</em>} />
              <Row label="Rail" value={<Badge variant="outline">{rail.toUpperCase()}</Badge>} />
              <Row label="Beneficiary" value={eligibleBenes.find((b) => b.id === beneId)?.nickname || <em className="text-muted-foreground">—</em>} />
              <Row label="Amount" value={<span className="font-mono">{formatINR(parseFloat(amount) || 0)}</span>} />
              <Row label="PG fee" value={<span className="font-mono text-success">+ {formatINR(pgFee)}</span>} />
              <div className="border-t border-border pt-3 flex items-center justify-between">
                <span className="text-sm font-semibold">Customer pays</span>
                <span className="font-mono font-bold text-lg tabular-nums" data-testid="collected-total">{formatINR(collectedTotal)}</span>
              </div>
              <div className="text-[11px] text-muted-foreground">After collection, the payout enters L1 → L2 → L3 admin approval. Final approval disburses {formatINR(parseFloat(amount) || 0)} to the beneficiary and credits {formatINR(pgFee)} to your wallet.</div>
            </div>
          </aside>
        </div>
      </div>

      {/* Add bene inline */}
      <Dialog open={addBeneOpen} onOpenChange={(v) => { setAddBeneOpen(v); if (!v) { setAddBeneOtpStep("idle"); setAddBeneOtp(""); setTutorMobile(""); setTutorKyc(null); setTutorAccounts(null); setKycLinkData(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isEducation ? `Add tutor for +91 ${customerMobile}` : `Add beneficiary for +91 ${customerMobile}`}</DialogTitle>
            <DialogDescription>
              {addBeneOtpStep === "otp_required"
                ? "Enter the parent-system OTP to verify the customer & save this account."
                : "OTP already verified · linked to this customer."}
            </DialogDescription>
          </DialogHeader>
          {addBeneOtpStep === "otp_required" ? (
            // Inline OTP step — only shown when hitch.zone session has expired
            // and the partner needs to re-authenticate the customer to add a
            // bank account.
            <div className="space-y-3">
              <div className="rounded-md border border-border bg-muted/40 p-3 text-xs">
                Sent to <span className="font-mono font-semibold">+91 {customerMobile}</span> via the parent system. Hitch.zone OTP expires in ~1 minute — enter it quickly.
              </div>
              <Label>OTP</Label>
              <Input
                value={addBeneOtp}
                onChange={(e) => setAddBeneOtp(e.target.value.replace(/\D/g, ""))}
                maxLength={6}
                className="font-mono text-center text-lg"
                data-testid="add-bene-hitch-otp"
                placeholder="••••••"
              />
            </div>
          ) : (
            <>
              {(subscription?.bene_add_fee || 0) > 0 && (
                <div className="rounded-md border border-warning/40 bg-warning/5 p-3 text-xs flex items-start gap-2" data-testid="bene-add-fee-notice">
                  <AlertCircle className="h-4 w-4 text-warning mt-0.5" />
                  <div>
                    <span className="font-semibold">Heads up — wallet debit:</span> A one-time beneficiary-add fee of{" "}
                    <span className="font-mono font-bold">{formatINR(subscription.bene_add_fee)}</span> will be deducted from your wallet when you save this beneficiary.
                  </div>
                </div>
              )}
              <div className="space-y-3">
                {isEducation && (
                  <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="tutor-kyc-block">
                    <div className="flex items-center gap-2 text-xs font-semibold">
                      <ShieldCheck className="h-4 w-4" /> Tutor verification (required for education fees)
                    </div>
                    <Label>Tutor mobile</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        value={tutorMobile}
                        onChange={(e) => {
                          setTutorMobile(e.target.value.replace(/\D/g, "").slice(0, 10));
                          setTutorKyc(null);
                        }}
                        maxLength={10}
                        placeholder="10-digit tutor mobile"
                        className="font-mono"
                        data-testid="tutor-mobile-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={checkTutorKyc}
                        disabled={tutorKycBusy || tutorMobile.length !== 10}
                        data-testid="check-tutor-kyc-btn"
                      >
                        {tutorKycBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check KYC"}
                      </Button>
                    </div>
                    {tutorKyc && (
                      <div
                        className={`rounded-md p-2 text-xs ${
                          tutorKyc.is_verified
                            ? "bg-emerald-50 border border-emerald-300 text-emerald-900"
                            : "bg-rose-50 border border-rose-300 text-rose-900"
                        }`}
                        data-testid="tutor-kyc-result"
                      >
                        <div className="font-semibold">
                          {tutorKyc.is_verified ? "Verified" : "Not eligible"} · {tutorKyc.verification_percentage}% (needs ≥ {tutorKyc.threshold}%)
                        </div>
                        {tutorKyc.full_name && <div className="mt-0.5">Name: {tutorKyc.full_name}</div>}
                        {tutorKyc.pan_number && <div>PAN: {tutorKyc.pan_number}</div>}
                        {!tutorKyc.is_verified && (
                          <div className="mt-1 space-y-2">
                            <div className="text-[11px]">Ask the tutor to complete hitch.zone KYC, then re-check.</div>
                            {/* Phase 7.37s — one-click create + share KYC link so the partner
                                doesn't have to bounce to /kyc-links and re-enter the mobile. */}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full"
                              data-testid="create-tutor-kyc-link-btn"
                              onClick={() => createAndShareKycLink({
                                mobile: tutorMobile, purpose: "tutor",
                                note: `Tutor KYC for student +91 ${customerMobile}`,
                              })}
                            >
                              <Link2 className="h-4 w-4 mr-1" />
                              Create KYC link & share on WhatsApp
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* KYC verified → option to import existing accounts saved for this tutor */}
                    {tutorKyc?.is_verified && (
                      <div className="space-y-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={loadTutorAccounts}
                          disabled={tutorAccountsBusy || tutorOtpStep === "sent"}
                          className="w-full"
                          data-testid="import-verified-account-btn"
                        >
                          {tutorAccountsBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
                          Import verified account (sends OTP to tutor)
                        </Button>

                        {/* OTP input — appears after we trigger hitch.zone send-otp */}
                        {tutorOtpStep === "sent" && (
                          <div className="rounded-md border border-primary/30 bg-background p-2 space-y-2" data-testid="tutor-otp-block">
                            <Label className="text-[11px]">Enter OTP sent to +91 {tutorMobile}</Label>
                            <div className="flex gap-2">
                              <Input
                                value={tutorOtp}
                                onChange={(e) => setTutorOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                maxLength={6}
                                placeholder="6-digit OTP"
                                className="font-mono"
                                data-testid="tutor-otp-input"
                              />
                              <Button
                                type="button"
                                size="sm"
                                onClick={verifyTutorOtpAndLoadAccounts}
                                disabled={tutorOtpBusy || tutorOtp.length < 4}
                                data-testid="tutor-otp-verify"
                              >
                                {tutorOtpBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & load"}
                              </Button>
                            </div>
                            <button
                              type="button"
                              className="text-[10px] text-muted-foreground hover:text-foreground"
                              onClick={() => { setTutorOtpStep("idle"); setTutorOtp(""); }}
                            >
                              ← Cancel
                            </button>
                          </div>
                        )}

                        {Array.isArray(tutorAccounts) && tutorAccounts.length > 0 && (
                          <div className="rounded-md border border-border bg-background p-2 space-y-1.5 max-h-44 overflow-y-auto" data-testid="tutor-accounts-list">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Saved tutor accounts · tap to import</div>
                            {tutorAccounts.map((acc) => (
                              <button
                                key={acc.id}
                                type="button"
                                className="w-full text-left rounded-md border border-border hover:border-primary hover:bg-primary/5 p-2 transition-colors"
                                onClick={() => applyTutorAccount(acc)}
                                data-testid={`tutor-account-${acc.id}`}
                              >
                                <div className="text-xs font-semibold flex items-center gap-1">
                                  {acc.nickname}
                                  <span className="text-[10px] font-normal text-muted-foreground uppercase">· {acc.type}</span>
                                </div>
                                <div className="text-[11px] text-muted-foreground font-mono">
                                  {acc.type === "bank"
                                    ? `${acc.account_number_masked} · ${acc.ifsc}`
                                    : acc.upi_id}
                                </div>
                                {acc.account_holder_name && (
                                  <div className="text-[10px] text-muted-foreground">{acc.account_holder_name}</div>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* KYC missing / partial → share a hitch.zone KYC + bank onboarding link */}
                    {tutorKyc && !tutorKyc.is_verified && (
                      <div className="space-y-2 mt-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => generateTutorKycLink(true)}
                          disabled={kycLinkBusy}
                          className="w-full"
                          data-testid="send-kyc-link-btn"
                        >
                          {kycLinkBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Link2 className="h-4 w-4 mr-1" />}
                          Send KYC + bank link to tutor
                        </Button>
                        {kycLinkData && (
                          <div className="rounded-md border border-amber-300 bg-amber-50 p-2 space-y-1.5" data-testid="kyc-link-panel">
                            <div className="text-[10px] uppercase tracking-wider text-amber-900">
                              Share with tutor · they complete KYC + add bank, then re-check above
                            </div>
                            <div className="font-mono text-[11px] break-all bg-background border rounded p-1.5">
                              {kycLinkData.url}
                            </div>
                            <div className="flex gap-1">
                              <Button type="button" size="sm" variant="outline" className="flex-1" onClick={copyKycLink} data-testid="copy-kyc-link">
                                <Copy className="h-3 w-3 mr-1" /> Copy
                              </Button>
                              <a
                                href={kycLinkData.whatsapp_url}
                                target="_blank"
                                rel="noreferrer"
                                className="flex-1 inline-flex items-center justify-center rounded-md border border-emerald-500 text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 text-xs font-medium"
                                data-testid="share-whatsapp-kyc-link"
                              >
                                <MessageCircle className="h-3 w-3 mr-1" /> WhatsApp
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <Label>{isEducation ? "Tutor nickname" : "Nickname"}</Label>
                  <Input value={bneNickname} onChange={(e) => setBneNickname(e.target.value)} data-testid="bne-nick" />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={bneType} onValueChange={setBneType}>
                    <SelectTrigger data-testid="bne-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="bank">Bank</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {bneType === "upi" ? (
                  <div>
                    <Label>UPI ID</Label>
                    <Input value={bneUpi} onChange={(e) => setBneUpi(e.target.value)} data-testid="bne-upi" placeholder="name@upi" />
                  </div>
                ) : (
                  <>
                    <div>
                      <Label>Account number</Label>
                      <Input value={bneAcc} onChange={(e) => setBneAcc(e.target.value)} data-testid="bne-acc" className="font-mono" />
                    </div>
                    <div>
                      <Label>IFSC</Label>
                      <Input value={bneIfsc} onChange={(e) => setBneIfsc(e.target.value.toUpperCase())} maxLength={11} data-testid="bne-ifsc" className="font-mono" />
                    </div>
                    <div>
                      <Label>
                        Account holder name <span className="text-[10px] text-muted-foreground">(auto-filled from tutor's hitch.zone KYC when blank)</span>
                      </Label>
                      <Input value={bneHolder} onChange={(e) => setBneHolder(e.target.value)} data-testid="bne-holder" placeholder="Leave empty — fetched from KYC" />
                    </div>
                  </>
                )}
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddBeneOpen(false)}>Cancel</Button>
            {addBeneOtpStep === "otp_required" ? (
              <Button onClick={verifyAndAddBene} disabled={busy || addBeneOtp.length < 4} data-testid="bne-save-otp">
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                Verify & add
              </Button>
            ) : (
              <Button onClick={addBeneficiary} disabled={busy || !bneNickname || (isEducation && !tutorKyc?.is_verified)} data-testid="bne-save">{isEducation ? "Add tutor" : "Add"}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hitch.zone "Import saved accounts" dialog */}
      <Dialog open={hitchImportOpen} onOpenChange={(v) => { setHitchImportOpen(v); if (!v) { setHitchImportStep("idle"); setHitchOtp(""); } }}>
        <DialogContent className="max-w-md" data-testid="hitch-import-dialog">
          <DialogHeader>
            <DialogTitle>Import customer's saved accounts</DialogTitle>
            <DialogDescription>
              We'll ask the customer for an OTP to log into the parent system, then pull every bank account they've already verified there.
            </DialogDescription>
          </DialogHeader>
          {hitchImportStep === "idle" ? (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-border bg-muted/40 p-3 text-xs">
                Customer mobile: <span className="font-mono font-semibold">+91 {customerMobile}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                If the customer is already logged in (within the last {Math.round(180 / 60)} h) we'll skip the OTP and import directly.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <Label>Enter OTP from parent system</Label>
              <Input value={hitchOtp} onChange={(e) => setHitchOtp(e.target.value.replace(/\D/g, ""))} maxLength={6} className="font-mono text-center text-lg" data-testid="hitch-otp-input" placeholder="••••••" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHitchImportOpen(false)}>Cancel</Button>
            {hitchImportStep === "idle" ? (
              <Button onClick={startHitchImport} disabled={hitchImportBusy} data-testid="hitch-import-start">
                {hitchImportBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                Send OTP & import
              </Button>
            ) : (
              <Button onClick={completeHitchImport} disabled={hitchImportBusy || hitchOtp.length < 4} data-testid="hitch-import-verify">
                {hitchImportBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                Verify & import
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link result */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Share this payment link</DialogTitle>
            <DialogDescription>Customer (+91 {customerMobile}) pays {formatINR(collectedTotal)}. We'll notify you when paid.</DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-border p-3 bg-muted/40 font-mono text-xs break-all" data-testid="link-value">{linkUrl}</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(linkUrl); toast.success("Copied"); }} data-testid="copy-link"><Copy className="h-4 w-4 mr-1" /> Copy</Button>
            <a href={linkUrl} target="_blank" rel="noopener noreferrer">
              <Button data-testid="open-link"><ExternalLink className="h-4 w-4 mr-1" /> Open</Button>
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

const Stepper = ({ step }) => {
  const steps = ["Mobile", "Beneficiary", "Amount & Pay"];
  return (
    <div className="mt-6 flex items-center gap-2 flex-wrap">
      {steps.map((s, i) => {
        const idx = i + 1;
        const active = step === idx;
        const done = step > idx;
        return (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${active ? "border-primary bg-primary/10 text-primary" : done ? "border-success/50 bg-success/10 text-success" : "border-border text-muted-foreground"}`}>
              {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="font-mono">{idx}</span>}
              {s}
            </div>
            {i < steps.length - 1 && <div className={`h-px w-6 ${done ? "bg-success/40" : "bg-border"}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const BackButton = ({ onClick }) => (
  <button type="button" onClick={onClick} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3">
    <ArrowLeft className="h-3 w-3" /> Back
  </button>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span>{value}</span>
  </div>
);
